import React, { useState, useEffect, useCallback } from 'react'
import styled from '@emotion/styled'
import { IoWarningOutline } from 'react-icons/io5'
import { FaArrowRight } from 'react-icons/fa6'
import { LuDollarSign, LuMail } from 'react-icons/lu'

import { Dropdown, CustomCurrencyInput } from '@components/common/form'
import Loader from '@components/common/Loader'
import { CancelledGroupOrderTable } from '@containers/groupOrders'
import {
  GroupOrder,
  ClientGlobalSettings,
  CancellationReq,
  CancellationPreviewResp,
  CancelledObjectType,
  CancellationType,
  CancellationReason,
  CancellationResp,
  CancelGroupOrderObjectReq,
  CancelTriggerSource,
  CancellationReasonObj,
  CancelledObject,
} from '@types'
import { colors } from '@constants'

const CANCELLATION_REASONS = [
  {
    label: 'Vendor Cancellation - Ingredient/Supply Issue',
    value: CancellationReason.VENDOR_SUPPLY,
    cancellationType: CancellationType.PRE_CUTOFF_VENDOR,
  },
  {
    label: 'Vendor Cancellation - Staffing Issue',
    value: CancellationReason.VENDOR_STAFFING,
    cancellationType: CancellationType.PRE_CUTOFF_VENDOR,
  },
  {
    label: 'Vendor Cancellation - Low Volume Order',
    value: CancellationReason.VENDOR_LOW_VOLUME,
    cancellationType: CancellationType.PRE_CUTOFF_VENDOR,
  },
  {
    label: 'Vendor Cancellation - Other',
    value: CancellationReason.VENDOR_OTHER,
    cancellationType: CancellationType.PRE_CUTOFF_VENDOR,
  },
]

const isVendorCancellation = (reason: string | undefined) => {
  return reason?.startsWith('vendor_cancellation_') || false
}

const pCancelType = (type: CancelledObjectType) => {
  switch (type) {
    case CancelledObjectType.ITEM:
      return 'menu item'
    case CancelledObjectType.MENU:
      return 'menu'
    case CancelledObjectType.VENDOR:
      return 'vendor'
  }
}

type GroupOrderPartialRefundModalProps = {
  groupOrder: GroupOrder
  clientGlobalSettings: ClientGlobalSettings | null
  cancelledObject: CancelledObject
  cancellationTimingStatus: string
  onReloadDates: () => void
  loadCancelPreview: (
    req: CancellationReq,
  ) => Promise<CancellationPreviewResp | null>
  cancelGroupOrderObject: (
    req: CancelGroupOrderObjectReq,
  ) => Promise<CancellationResp | null>
  close: () => void
  flashSuccessMessage: (message: string) => void
  flashFailureMessage: (message: string) => void
}

const GroupOrderPartialRefundModal: React.FC<
  GroupOrderPartialRefundModalProps
> = ({
  groupOrder,
  clientGlobalSettings,
  cancelledObject,
  cancellationTimingStatus,
  onReloadDates,
  loadCancelPreview,
  cancelGroupOrderObject,
  close,
  flashSuccessMessage,
  flashFailureMessage,
}) => {
  const [stage, setStage] = useState<'confirm' | 'preview'>('confirm')
  const [cancellationReason, setCancellationReason] =
    useState<CancellationReasonObj | null>(null)

  const onChangeCancellationReason = (reason: CancellationReasonObj | null) => {
    setCancellationReason(reason)
  }

  switch (stage) {
    case 'confirm':
      return (
        <GroupOrderPartialRefundConfirmStage
          cancelledObject={cancelledObject}
          cancellationTimingStatus={cancellationTimingStatus}
          cancellationReason={cancellationReason}
          onChangeCancellationReason={onChangeCancellationReason}
          nextStage={() => setStage('preview')}
          close={close}
        />
      )
    case 'preview':
      return (
        <GroupOrderPartialRefundPreviewStage
          groupOrder={groupOrder}
          clientGlobalSettings={clientGlobalSettings}
          cancelledObject={cancelledObject}
          cancellationReason={cancellationReason}
          onReloadDates={onReloadDates}
          loadCancelPreview={loadCancelPreview}
          cancelGroupOrderObject={cancelGroupOrderObject}
          previousStage={() => setStage('confirm')}
          flashSuccessMessage={flashSuccessMessage}
          flashFailureMessage={flashFailureMessage}
          close={close}
        />
      )
    default:
      return null
  }
}

export default GroupOrderPartialRefundModal

const GroupOrderPartialRefundConfirmStage: React.FC<{
  cancelledObject: CancelledObject
  cancellationTimingStatus: string
  cancellationReason: CancellationReasonObj | null
  nextStage: () => void
  onChangeCancellationReason: (reason: CancellationReasonObj | null) => void
  close: () => void
}> = ({
  cancelledObject,
  cancellationTimingStatus,
  cancellationReason,
  nextStage,
  onChangeCancellationReason,
  close,
}) => {
  return (
    <div className="w-full h-full">
      <div className="w-full flex flex-col">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row items-center">
            <IoWarningOutline className="text-4xl text-yellow-500 mr-2" />
            <span className="text-lg font-bold">
              Select Cancellation Reason
            </span>
          </div>
          <button className="text-gray-500 text-2xl" onClick={() => close()}>
            ✕
          </button>
        </div>
        <p>
          You are about to delete{' '}
          <span className="text-red-500">
            only this {pCancelType(cancelledObject.type)}:
          </span>{' '}
          {cancelledObject.name}
          {cancelledObject.from ? ` from the menu ${cancelledObject.from}` : ''}
        </p>
      </div>
      <div
        style={{ height: '60vh', overflowY: 'auto' }}
        className="flex flex-col mt-3 items-center"
      >
        <p>
          Current Cancellation Timing Status:{' '}
          <span className="text-red-500">{cancellationTimingStatus}</span>
        </p>
        <div
          className="rounded-md p-4 my-2 bg-red-100 flex flex-col justify-start"
          style={{ width: '60%', border: '2px solid #f56565' }}
        >
          <p className="text-red-500 mb-2 font-bold">
            Why are you removing this {pCancelType(cancelledObject.type)}?
          </p>
          <Dropdown
            label="Select Cancellation Reason *"
            width="100%"
            labelStyle={{ color: '#f56565' }}
            value={cancellationReason?.value || ''}
            onChange={(e) =>
              onChangeCancellationReason(
                CANCELLATION_REASONS.find(
                  (reason) => reason.value === e.target.value,
                ) || null,
              )
            }
          >
            <option value="">Select Cancellation Reason</option>
            {CANCELLATION_REASONS.map((reason) => (
              <option key={reason.value} value={reason.value}>
                {reason.label}
              </option>
            ))}
          </Dropdown>
          {cancellationReason && (
            <div
              className="bg-white rounded-md p-2 w-full"
              style={{ border: '2px solid #f56565' }}
            >
              <p className="font-bold text-sm text-red-500">Selected Reason:</p>
              {cancellationReason.label && (
                <p className="text-sm text-red-800 mt-2">
                  {cancellationReason.label}
                </p>
              )}
              {isVendorCancellation(cancellationReason.value) && (
                <div
                  className="mt-2 bg-red-100 rounded-md"
                  style={{ border: '1px solid #f56565', padding: '4px' }}
                >
                  <p className="text-sm text-red-800 font-bold">
                    Vendor payout for this order will be voided.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        <div
          className="flex flex-col rounded-md p-4 mt-4 bg-blue-100"
          style={{ width: '60%', border: '2px solid #4299e1' }}
        >
          <p className="font-bold text-blue-500">What happens next?</p>
          <p className="text-blue-500">
            {"1. You'll see which orders will be impacted by this cancellation"}
          </p>
          <p className="text-blue-500">
            2. Review the financial impact including refunds and any applicable
            charges
          </p>
          <p className="text-blue-500">
            3. Confirm the cancellation and automatic notifications will be sent
          </p>
        </div>
      </div>
      <div className="flex flex-row justify-end w-full">
        <button
          className="big-white border-2 border-gray-300 px-4 py-2 rounded-md"
          onClick={close}
        >
          Cancel
        </button>
        <button
          className="text-white px-4 py-2 rounded-md flex flex-row items-center justify-center ml-2"
          style={{
            backgroundColor: colors.violet,
            opacity: !cancellationReason ? 0.5 : 1,
          }}
          onClick={nextStage}
          disabled={!cancellationReason}
        >
          Continue to Review Impact <FaArrowRight className="ml-2" />
        </button>
      </div>
    </div>
  )
}

const GroupOrderPartialRefundPreviewStage: React.FC<{
  groupOrder: GroupOrder
  clientGlobalSettings: ClientGlobalSettings | null
  cancelledObject: CancelledObject
  cancellationReason: CancellationReasonObj | null
  onReloadDates: () => void
  loadCancelPreview: (
    req: CancellationReq,
  ) => Promise<CancellationPreviewResp | null>
  cancelGroupOrderObject: (
    req: CancelGroupOrderObjectReq,
  ) => Promise<CancellationResp | null>
  previousStage: () => void
  flashSuccessMessage: (message: string) => void
  flashFailureMessage: (message: string) => void
  close: () => void
}> = ({
  groupOrder,
  clientGlobalSettings,
  cancelledObject,
  cancellationReason,
  onReloadDates,
  loadCancelPreview,
  cancelGroupOrderObject,
  previousStage,
  flashSuccessMessage,
  flashFailureMessage,
  close,
}) => {
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<CancellationPreviewResp | null>(null)
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)
  const [chefPayoutAdjustment, setChefPayoutAdjustment] = useState<number>(0)
  const [revenueImpact, setRevenueImpact] = useState<number | undefined>(
    undefined,
  )
  const [refundProcessing, setRefundProcessing] = useState<boolean>(false)

  const _loadCancelPreview = useCallback(async () => {
    setLoading(true)
    const req: CancellationReq = {
      cancelledObjectType: cancelledObject.type,
      cancelledObjectId: cancelledObject.id,
    }

    const preview = await loadCancelPreview(req)
    if (preview) {
      setPreview(preview)
      setRevenueImpact(preview?.financialImpacts.netRevenueImpact ?? 0)
    }
    setLoading(false)
  }, [cancelledObject.type, cancelledObject.id, loadCancelPreview])

  useEffect(() => {
    _loadCancelPreview()
  }, [_loadCancelPreview])

  const onCancelGroupOrderObject = async () => {
    if (!cancellationReason || !groupOrder) {
      return
    }
    setRefundProcessing(true)

    const { id: cancelledId, type: cancelledType, scope } = cancelledObject

    const resp = await cancelGroupOrderObject({
      grpId: groupOrder.id,
      cancelledObjectId: cancelledId,
      cancelledObjectType: cancelledType,
      cancellationScope: scope,
      cancellationType: cancellationReason.cancellationType,
      cancellationReason: cancellationReason.value,
      chefPayoutAdjustment: chefPayoutAdjustment,
      subOrderIds: preview?.subOrderIds ?? [],
      sendChefCancellationEmail: true,
      triggerSource: CancelTriggerSource.ADMIN,
    })
    if (resp) {
      flashSuccessMessage(
        'Cancellations successfully processed. You will be emailed final results shortly.',
      )
      onReloadDates()
      close()
    } else {
      flashFailureMessage('Failed to process cancellations. Please try again.')
    }
    setRefundProcessing(false)
  }

  const renderOrderImpact = (
    quantity: number,
    label: string,
    color: string,
  ) => {
    return (
      <div className="flex flex-col">
        <p className={`text-xl font-bold ${color}`}>{quantity}</p>
        <p className="text-sm text-yellow-800">{label}</p>
      </div>
    )
  }

  const renderRevenueImpact = (netRevenueImpact: number) => {
    let color = 'text-green-500'
    let sign = '+'
    if (netRevenueImpact < 0) {
      color = 'text-red-500'
      sign = '-'
    }

    return (
      <p className={`font-bold text-xl ${color}`}>
        {sign}${Math.abs(Number(netRevenueImpact)).toFixed(2)}
      </p>
    )
  }

  const onChangeChefPayoutAdjustment = (value: number) => {
    const originalRevenueImpact =
      preview?.financialImpacts.netRevenueImpact ?? 0

    // revenue impact here is exclusive of vendor payouts coming from the backend
    const newRevenueImpact = originalRevenueImpact - value
    setChefPayoutAdjustment(value)
    setRevenueImpact(newRevenueImpact)
  }

  const toggleConfirmationModal = (open: boolean) => {
    const dialog = document.getElementById(
      'group-order-confirm-cancellation-modal',
    ) as HTMLDialogElement | null
    if (dialog && open) {
      dialog.showModal()
      setConfirmationDialogOpen(true)
    } else if (dialog && !open) {
      dialog.close()
      setConfirmationDialogOpen(false)
    }
  }

  const renderConfirmationModal = () => {
    return (
      <dialog
        id="group-order-confirm-cancellation-modal"
        className="flex flex-col rounded-md p-4"
        style={{
          width: '33%',
          height: '80vh',
          display: confirmationDialogOpen ? 'block' : 'none', // dialog rendering as div below content instead or respecting the modal styles (maybe firefox issue)
        }}
      >
        <div className="flex flex-row items-center">
          <IoWarningOutline className="text-4xl text-red-500 mr-2" />
          <span className="text-lg font-bold">Confirm Deletion</span>
        </div>
        <p className="my-4">
          Are you sure you want to delete{' '}
          <span className="text-red-500 font-bold">
            {preview?.orderImpacts.totalItemsDeleted ?? 0}
          </span>{' '}
          item(s) that will impact{' '}
          <span className="text-red-500 font-bold">
            {preview?.orderImpacts.totalImpactedOrders ?? 0}
          </span>{' '}
          order(s)?
        </p>
        <div
          className="flex flex-col bg-red-200 my-2 rounded-md"
          style={{ border: '2px solid #f56565', padding: '10px' }}
        >
          <p className="text-red-500 font-bold">Cancellation Reason:</p>
          <p className="text-red-500 text-lg">{cancellationReason?.label}</p>
        </div>
        <div
          className="flex flex-col my-2 bg-purple-200 rounded-md"
          style={{ border: '2px solid #9f7aea', padding: '10px' }}
        >
          <p className="text-purple-500 font-bold">Refund Policy:</p>
          <p className="text-purple-500 text-lg">
            Impacted items will be removed from chef docs and live menus
          </p>
        </div>
        <div
          className="flex flex-col bg-orange-100 rounded-md"
          style={{ border: '2px solid #ed8936', padding: '10px' }}
        >
          <p className="text-orange-500 font-bold">Chef Payout Adjustment:</p>
          <p className="text-orange-500 text-lg">
            ${chefPayoutAdjustment.toFixed(2)}
          </p>
          <p className="text-orange-500 text-sm">
            This amount will be paid to the chef for this cancellation
          </p>
        </div>
        <div
          className="flex flex-row items-start my-2 bg-blue-200 rounded-md"
          style={{ border: '2px solid #4299e1', padding: '10px' }}
        >
          <LuMail className="text-blue-500 text-2xl mr-2" />
          <div className="flex flex-col">
            <p className="text-blue-500">
              <span className="text-blue-500 font-bold">
                Email notifications will be sent
              </span>{' '}
              to all impacted users informing them of the cancellation and
              refund details.
            </p>
            <p className="text-blue-500 font-bold my-2">
              Employee Refunds will be processed via Square within 3-5 business
              days.
            </p>
          </div>
        </div>
        <p className="my-2">
          This action cannot be undone. All cancellation details will be
          recorded in the Audit Log.
        </p>
        <div className="flex flex-row items-center justify-end gap-2">
          <button
            className="big-white border-2 border-gray-300 px-4 py-2 rounded-md"
            style={{ opacity: refundProcessing ? 0.5 : 1 }}
            onClick={() => toggleConfirmationModal(false)}
            disabled={refundProcessing}
          >
            Cancel
          </button>
          <button
            className="text-white px-4 py-2 rounded-md flex flex-row items-center justify-center ml-2"
            style={{
              backgroundColor: colors.violet,
              opacity: !cancellationReason || refundProcessing ? 0.5 : 1,
              minWidth: '250px',
            }}
            disabled={!cancellationReason || refundProcessing}
            onClick={() => onCancelGroupOrderObject()}
          >
            {refundProcessing
              ? 'Processing...'
              : 'Yes, Delete & Send Notifications'}
          </button>
        </div>
      </dialog>
    )
  }

  return (
    <div className="w-full h-full">
      <div className="w-full flex flex-row justify-between">
        <div className="flex flex-row items-center">
          <IoWarningOutline className="text-4xl text-yellow-500 mr-2" />
          <span className="text-lg font-bold">
            Delete {pCancelType(cancelledObject.type)} - Review Impacted Orders
          </span>
        </div>
        <button className="text-gray-500 text-2xl" onClick={() => close()}>
          ✕
        </button>
      </div>
      <div style={{ height: '85%', overflowY: 'auto' }}>
        <div
          className="flex flex-row items-center bg-red-100 rounded-md p-4 my-2 justify-between"
          style={{ border: '2px solid #f56565' }}
        >
          <div className="flex flex-col">
            <p className="text-red-500 font-bold">Cancellation Reason:</p>
            <p className="text-red-500">{cancellationReason?.label}</p>
          </div>
          <button
            className="bg-white text-red-500 px-4 py-2 rounded-md"
            style={{ border: '1px solid #f56565' }}
            onClick={previousStage}
          >
            Change Reason
          </button>
        </div>
        {loading || !preview ? (
          <div className="flex flex-row items-center">
            <Loader width="40%" isCenter />
          </div>
        ) : (
          <div
            className="flex flex-col w-full"
            style={{ height: '60vh', overflowY: 'auto' }}
          >
            <div
              className="bg-yellow-100 rounded-md p-4 my-2 justify-between"
              style={{ border: '2px solid #ecc94b' }}
            >
              <div className="flex flex-col">
                <div className="flex flex-row items-center">
                  <IoWarningOutline className="text-4xl text-yellow-600 mr-2" />
                  <span className="text-lg font-bold text-yellow-800">
                    Warning: Orders Will Be Impacted
                  </span>
                </div>
                <div
                  className="mx-4 my-3 p-2 flex flex-row items-center justify-between rounded-md bg-white"
                  style={{ border: '1px solid #ecc94b' }}
                >
                  {renderOrderImpact(
                    preview?.orderImpacts.totalImpactedOrders,
                    'Orders Impacted',
                    'text-yellow-800',
                  )}
                  {renderOrderImpact(
                    preview?.orderImpacts.totalItemsDeleted,
                    'Items Being Deleted',
                    'text-red-500',
                  )}
                  {renderOrderImpact(
                    preview?.orderImpacts.totalCancelledOrders,
                    'Cancelled',
                    'text-red-700',
                  )}
                  {renderOrderImpact(
                    preview?.orderImpacts.totalPartialUpdateOrders,
                    'Partial Updates',
                    'text-orange-600',
                  )}
                </div>
                <p className="text-yellow-800 mx-4">
                  Deleting this {pCancelType(cancelledObject.type)} will cancel
                  all items in {preview?.orderImpacts.totalImpactedOrders}{' '}
                  order(s).
                </p>
              </div>
            </div>
            <div
              className="bg-blue-100 rounded-md p-4 my-2 justify-between w-full"
              style={{ border: '2px solid #4299e1' }}
            >
              <div className="flex flex-col w-full">
                <div className="flex flex-row items-center">
                  <LuDollarSign className="text-xl text-blue-500 mr-2" />
                  <span className="text-lg font-bold text-blue-500">
                    Financial Impact Summary
                  </span>
                </div>
                <FinancialImpactHeader
                  margin="5px 10px 0"
                  backgroundColor="#f56565"
                  className="mx-4"
                >
                  <span>1</span>Costs (What We Owe)
                </FinancialImpactHeader>
                <div className="mx-4 my-2 flex flex-row items-center justify-between">
                  <div
                    className="flex flex-col rounded-md bg-white p-2"
                    style={{ border: '1px solid #f56565', width: '40%' }}
                  >
                    <p>Employee Refunds</p>
                    <p className="text-red-500 text-xl font-bold">
                      -$
                      {preview?.financialImpacts.totalEmployeeRefunds.toFixed(
                        2,
                      )}
                    </p>
                    <p className="text-sm">Processed in 3-5 business days</p>
                  </div>
                  <div
                    className="flex flex-col rounded-md bg-white p-2"
                    style={{ border: '1px solid #ed8936', width: '40%' }}
                  >
                    <p>Employer Subsidy Adjustment</p>
                    <p className="text-orange-500 text-xl font-bold">
                      -$
                      {preview?.financialImpacts.totalSubsidyAdjustment.toFixed(
                        2,
                      )}
                    </p>
                    <p className="text-sm">Not charged to client</p>
                  </div>
                </div>
                <FinancialImpactHeader
                  margin="5px 0 10px 0"
                  backgroundColor="#48bb78"
                  className="mx-4"
                >
                  <span>2</span>Chef Payout Adjustment
                </FinancialImpactHeader>
                <div
                  className="mx-4 my-2 flex flex-col rounded-md bg-white p-2"
                  style={{ border: '2px solid #48bb78' }}
                >
                  <div className="flex flex-row items-center justify-between">
                    <p>
                      Calculated: Chef cost x items removed ={' '}
                      <span className="font-bold">
                        $
                        {preview?.financialImpacts.chefPayoutAdjustment.toFixed(
                          2,
                        )}
                      </span>
                    </p>
                    <div className="flex flex-row gap-2 items-center mt-2">
                      {[0, 50, 100].map((chefPayAdj) => (
                        <button
                          key={`chef-pay-adj-${chefPayAdj}`}
                          className="bg-white font-bold px-2 py-1 rounded-md"
                          style={{ border: '1px solid #e2e8f0' }}
                          onClick={() => {
                            if (
                              !preview?.financialImpacts.chefPayoutAdjustment
                            ) {
                              return
                            }
                            onChangeChefPayoutAdjustment(
                              preview?.financialImpacts.chefPayoutAdjustment *
                                (chefPayAdj / 100),
                            )
                          }}
                        >
                          {chefPayAdj}%
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="my-3">
                    <CustomCurrencyInput
                      value={chefPayoutAdjustment}
                      onChange={(value) => {
                        onChangeChefPayoutAdjustment(value ?? 0)
                      }}
                      placeholder="$0.00"
                      decimals={2}
                      min={0}
                      prefix="$"
                    />
                  </div>
                  <p>
                    This amount will be <span className="font-bold">PAID</span>{' '}
                    to the chef for this cancellation
                  </p>
                </div>
                <div className="mx-4 my-2 flex flex-col rounded-md bg-gray-700 p-2">
                  <FinancialImpactHeader
                    margin="0 0 10px 0"
                    backgroundColor="white"
                    textColor="#4a5568"
                    color="white"
                    className="mx-4"
                  >
                    <span>3</span>Net Revenue Impact
                  </FinancialImpactHeader>
                  {renderRevenueImpact(revenueImpact ?? 0)}
                  <p className="text-white text-sm">
                    {`-$${preview.financialImpacts.totalEmployeeRefunds.toFixed(
                      2,
                    )} refunds - $${preview.financialImpacts.totalSubsidyAdjustment.toFixed(
                      2,
                    )} subsidy - $${chefPayoutAdjustment.toFixed(
                      2,
                    )} adjusted chef payout`}
                  </p>
                </div>
              </div>
            </div>
            <CancelledGroupOrderTable
              groupOrder={groupOrder}
              clientGlobalSettings={clientGlobalSettings}
              cancelledId={cancelledObject.id}
              cancelledType={cancelledObject.type}
              totalAffectedOrders={
                preview?.orderImpacts.totalImpactedOrders ?? 0
              }
            />
          </div>
        )}
      </div>
      <div className="flex flex-row justify-end w-full mt-4">
        <button
          className="big-white border-2 border-gray-300 px-4 py-2 rounded-md"
          onClick={previousStage}
        >
          Back
        </button>
        <button
          className="text-white px-4 py-2 rounded-md flex flex-row items-center justify-center ml-2"
          style={{ backgroundColor: colors.violet }}
          disabled={!cancellationReason}
          onClick={() => toggleConfirmationModal(true)}
        >
          Confirm Cancellation <FaArrowRight className="ml-2" />
        </button>
      </div>
      {renderConfirmationModal()}
    </div>
  )
}

export const FinancialImpactHeader = styled.h2<{
  margin?: string
  backgroundColor?: string
  textColor?: string
  color?: string
}>`
  font-family: 'bold';
  color: ${(props) => props.color || colors.gray400};
  font-size: 14px;
  display: flex;
  align-items: center;
  margin: ${(props) => props.margin};
  span {
    color: ${(props) => props.textColor || '#fff'};
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    min-width: 16px;
    height: 16px;
    background: ${(props) => props.backgroundColor || colors.orange};
    border-radius: 16px;
    margin-right: 10px;
  }
  .details {
    font-family: 'light';
    font-size: 18px;
    display: flex;
    align-items: center;
  }
  .message {
    font-family: 'bold';
    font-size: 14px;
    display: flex;
    align-items: center;
    text-decoration: underline;
  }
  .message:hover {
    cursor: pointer;
  }
`
